import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  api,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  Input,
  Label,
  LegalLinks,
  SignInFlow,
  SignUpAgreement,
} from '@dataroom/shared';
import { trackSignUp } from '@/lib/analytics';
import { captureReferral, clearStoredClick, readStoredClick } from '@/lib/referral';

// Matches the API (src/modules/auth/passwords.ts).
const MIN_PASSWORD = 10;

/**
 * Start a free trial: a new firm (tenant) with the visitor as its owner
 * (POST /auth/register-trial). The API emails a 6-digit code to confirm the
 * address, so on success the shared sign-in flow opens on its code screen.
 *
 * Affiliate referral (ADR-0042): a `?ref=`/`?click=` on the URL is captured on
 * arrival, and the newest stored click id (≤ 90 days) rides along with the
 * signup; the server decides whether it counts.
 *
 * Analytics (PROD only): the `sign_up` event is reported once the emailed code is
 * accepted — the account is confirmed — never on the button click.
 */
export function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [signedUpAs, setSignedUpAs] = useState<string | null>(null);

  // Once per URL: React StrictMode runs effects twice in development, and each
  // capture of a bare ?ref= records a click.
  const captured = useRef<string | null>(null);
  useEffect(() => {
    if (captured.current === location.search) return;
    captured.current = location.search;
    void captureReferral(location.search);
  }, [location.search]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters for your password.`);
      return;
    }
    setBusy(true);
    setError(null);
    const address = email.trim();
    const clickId = readStoredClick();
    api
      .post('/auth/register-trial', {
        email: address,
        password,
        ...(name.trim() ? { displayName: name.trim() } : {}),
        ...(clickId ? { affiliateClickId: clickId } : {}),
      })
      .then(() => {
        clearStoredClick();
        setSignedUpAs(address);
      })
      .catch(setError)
      .finally(() => setBusy(false));
  };

  if (signedUpAs) {
    return (
      <SignInFlow
        title="ClosingVault"
        subtitle="Sign in to manage your rooms."
        initialCode={{
          email: signedUpAs,
          notice: `Your free trial is ready. We emailed a 6-digit code to ${signedUpAs} — enter it to confirm your email.`,
        }}
        onEmailConfirmed={(address) => trackSignUp(address, location.search)}
        onSignedIn={() => navigate('/')}
      />
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <div className="w-full max-w-sm space-y-4">
        <Card className="w-full">
          <CardHeader>
            <CardTitle as="h1">Start your free trial</CardTitle>
            <p className="text-sm text-muted-foreground">
              30 days, one deal room, every feature. No credit card.
            </p>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={onSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="signup-name">Your name</Label>
                <Input
                  id="signup-name"
                  autoComplete="name"
                  maxLength={200}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-email">Work email</Label>
                <Input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-password">Password</Label>
                <Input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">At least {MIN_PASSWORD} characters.</p>
              </div>
              <ErrorText error={error} />
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? 'Starting…' : 'Start free trial'}
              </Button>
              <SignUpAgreement />
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  Sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
        <LegalLinks />
      </div>
    </div>
  );
}
